import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { api, ApiError, ensureCsrf } from '@/api/client';
import type { MessageResponse } from '@/api/types';

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const verify = useMutation({
    mutationFn: async () => {
      await ensureCsrf();
      return api<MessageResponse>('/auth/verify-email', { method: 'POST', body: { token } });
    },
    onSuccess: () => setState('ok'),
    onError: (err: unknown) => {
      setState('error');
      if (err instanceof ApiError) setErrorMessage(err.message);
    },
  });

  useEffect(() => {
    if (token) verify.mutate();
    // We deliberately only want this to fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-card__brand">
          <span className="auth-card__brand-mark" aria-hidden />
          <span>Polymarket Surveillance</span>
          <div className="spacer" />
          <ThemeToggle />
        </div>
        {!token ? (
          <>
            <h2 className="auth-card__title">Missing token</h2>
            <p className="auth-card__subtitle">Use the link in your verification email.</p>
            <Link to="/login">
              <Button variant="primary" block>
                Back to sign in
              </Button>
            </Link>
          </>
        ) : state === 'pending' ? (
          <>
            <h2 className="auth-card__title">Verifying…</h2>
            <p className="auth-card__subtitle">One moment.</p>
          </>
        ) : state === 'ok' ? (
          <>
            <h2 className="auth-card__title">Email verified</h2>
            <Badge tone="success">Active</Badge>
            <p className="auth-card__subtitle">
              Your account is active. You can now sign in and complete two-factor setup.
            </p>
            <Link to="/login">
              <Button variant="primary" block>
                Continue to sign in
              </Button>
            </Link>
          </>
        ) : (
          <>
            <h2 className="auth-card__title">Verification failed</h2>
            <p className="auth-card__subtitle">{errorMessage ?? 'The link is invalid or expired.'}</p>
            <Link to="/login">
              <Button variant="ghost" block>
                Back to sign in
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
